/**
 * ConcertHealthTab — iter86.31.14 (production Concert-plane landing tab).
 *
 * Rhythm (the deploy runbook), Pulse and Canon are rehearsal-plane tools —
 * they leave the production console entirely. In their place the concert
 * plane lands on this read-only verification surface.
 *
 * iter86.82.3 — Restructured per operator direction: ALL four live-posture
 * checks are Phase 3 ("Verify on production") evidence, numbered to match
 * the rehearsal-plane Rhythm gates:
 *   3.1 Production readiness  — banner, auto-fetches
 *   3.2 Post-deploy Auto-Verify — now AUTO-RUNS on load (was click-to-run)
 *   3.3 Vendor health          — renewal radar, Resend domain, API quota
 *   3.4 Game health            — detector verdict chip (deep-links Product Ops)
 * The operator ticks the matching 3.1–3.4 gates back on preview's Rhythm.
 */
import { HeartPulse, ClipboardCheck } from "lucide-react";
import VendorHealthCard from "@/components/site-admin/release-ops/VendorHealthCard";
import { GameHealthChipCard } from "@/components/site-admin/release-ops/GameHealthChipCard";
import DeploymentReadinessBanner from "@/components/site-admin/DeploymentReadinessBanner";
import PostDeployAutoVerifyCard from "@/components/site-admin/PostDeployAutoVerifyCard";
import { SubStep } from "@/components/site-admin/release-ops/Phase1PreCommitSafetyBody";

export default function ConcertHealthTab({ onOpenGameOps }) {
    return (
        <div className="space-y-4" data-testid="concert-health-tab">
            <div className="brutal-card p-5">
                <h2 className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                    <HeartPulse className="w-5 h-5 text-neon" /> Concert Health
                </h2>
                <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                    Live production posture at a glance. Deploy gates, Pulse and Canon governance
                    live on the rehearsal (preview) plane.
                </p>
            </div>

            {/* iter86.82.3 — all four checks ARE the Phase 3 verification
                evidence; numbered to mirror the Rhythm gates on preview. */}
            <div className="brutal-card p-5 space-y-5" data-testid="concert-release-verification">
                <div>
                    <h3 className="font-display font-extrabold text-sm uppercase flex items-center gap-2 mb-1">
                        <ClipboardCheck className="w-4 h-4 text-emerald-300" /> Release Verification · Phase 3 evidence
                    </h3>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                        After a deploy, every check below must be GREEN (or carry warnings you're
                        comfortable waiving). Then tick the matching gates on the rehearsal plane's
                        Rhythm runbook: <span className="text-zinc-200 font-bold">3.1 Production readiness</span> ·{" "}
                        <span className="text-zinc-200 font-bold">3.2 Post-deploy Auto-Verify</span> ·{" "}
                        <span className="text-zinc-200 font-bold">3.3 Vendor health</span> ·{" "}
                        <span className="text-zinc-200 font-bold">3.4 Game health</span>.
                    </p>
                </div>
                <SubStep n={1} title="Production readiness" phase={3} accentClass="text-emerald-400">
                    <DeploymentReadinessBanner />
                </SubStep>
                <SubStep n={2} title="Post-deploy Auto-Verify — runs automatically" phase={3} accentClass="text-emerald-400">
                    <PostDeployAutoVerifyCard autoRun />
                </SubStep>
                <SubStep n={3} title="Vendor health" phase={3} accentClass="text-emerald-400">
                    <VendorHealthCard />
                </SubStep>
                <SubStep n={4} title="Game health" phase={3} accentClass="text-emerald-400">
                    <GameHealthChipCard onOpenGameOps={onOpenGameOps} />
                </SubStep>
            </div>
        </div>
    );
}
